/**
 * The pieces the Pipeline & Facility AI pages draw (batch 6, fieldiq-api#1236):
 * the deterministic figures of each run as rows, the AI draft with its
 * review, and the two launchers on the job page (daily summary from the Daily
 * logs tab, job preflight from the Tickets tab). Used by the AI Drafts register,
 * a draft's page, the job page and the ticket page.
 *
 * THE FIGURES ARE THE API'S DETERMINISTIC LAYER, shown first and in full. The
 * AI wrote only the draft text below them. A pending draft reads "awaiting
 * your approval" and says nothing is published; it is approved (optionally
 * edited, right on the page — no modal) or dismissed through the PF doors. A run whose
 * draft failed still has correct figures, and says so with the API's reason.
 *
 * Every value that did not arrive renders `—`; a real 0 renders `0`.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, ListChecks, Loader2, Pencil, Sparkles, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfAiDigest, useReviewPfAi, useRunPfAi } from '../../../hooks/usePfAi';
import { usePfDailyLogs, usePfMeta } from '../../../hooks/usePipelineFacility';
import { catalogLabel, pfDay, withUnit } from '../../../lib/pipeline-facility';
import {
  PF_AI_MAX_NARRATIVE,
  PF_AI_MAX_OBSERVATION,
  pfAiRefusal,
  pfAiStateSkin,
  pfDigEventLabel,
} from '../../../lib/pf-ai';
import type {
  PfAiDraft,
  PfAiRunResult,
  PfComplianceDigestData,
  PfDailySummaryData,
  PfPreflightData,
  PfPreflightRateGap,
} from '../../../types/pf-ai';
import { PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfBadge, PfEmpty, PfField, PfFormError, PfPanel, PfSpec } from '../PfBits';

const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const text = (v: unknown): string => (typeof v === 'string' && v.trim() ? v : '—');

// ─── A small read-only list of rows ──────────────────────────────────────────

interface Col<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  align?: 'right';
}

function AiTable<T>({ rows, cols, rowKey, rail, empty, testId, minWidth = 'min-w-[640px]' }: {
  rows: T[];
  cols: Col<T>[];
  rowKey: (row: T, i: number) => string;
  rail?: (row: T) => string;
  empty: string;
  testId: string;
  minWidth?: string;
}) {
  if (rows.length === 0) return <PfEmpty>{empty}</PfEmpty>;
  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${minWidth} text-left`} data-testid={testId}>
        <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
          <tr className="border-l-4 border-l-transparent">
            {cols.map((c) => (
              <th key={c.key} scope="col" className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={rowKey(r, i)} data-testid={`${testId}-row`} className={`border-t border-l-4 border-fieldiq-border ${rail ? rail(r) : 'border-l-transparent'}`}>
              {cols.map((c) => (
                <td key={c.key} className={`px-4 py-3 text-sm text-fieldiq-text ${c.align === 'right' ? 'text-right font-mono tabular-nums' : ''}`}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const hours = (n: number | null | undefined) => withUnit(n, 'h');
const days = (list: unknown) => {
  const d = arr<string>(list);
  return d.length ? d.map((x) => pfDay(x)).join(', ') : '—';
};

// ─── Daily summary ───────────────────────────────────────────────────────────

export function DailySummaryFigures({ data }: { data: PfDailySummaryData }) {
  const crewRoles = usePfMeta().data?.data?.crewRoles;
  const crew = arr<PfDailySummaryData['crew'][number]>(data.crew);
  const units = arr<PfDailySummaryData['units'][number]>(data.units);
  const rentIns = arr<PfDailySummaryData['rentIns'][number]>(data.rentIns);
  const digs = arr<PfDailySummaryData['digs'][number]>(data.digs);
  const p = data.progress;
  return (
    <div className="space-y-4" data-testid="pf-ai-daily-figures">
      <PfPanel eyebrow="Daily summary" title={data.job?.jobNumber ? `${data.job.jobNumber} · ${pfDay(data.workDate)}` : pfDay(data.workDate)}>
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <PfSpec
            label="Job"
            value={data.job?.id ? <Link className="underline" to={`/pipeline-facility/jobs/${data.job.id}?tab=logs`}>{text(data.job.jobNumber)}</Link> : '—'}
            mono
          />
          <PfSpec label="Customer" value={text(data.job?.customer)} />
          <PfSpec label="Site" value={text(data.job?.site)} />
          <PfSpec label="Work date" value={pfDay(data.workDate)} />
          <PfSpec label="Weather" value={text(data.weather)} />
          <PfSpec label="Crew" value={displayValue(data.crewCount ?? undefined)} mono />
          <PfSpec label="Crew hours" value={hours(data.crewHours)} mono />
          <PfSpec label="Crew standby" value={hours(data.crewStandbyHours)} mono />
          <PfSpec label="Unit hours" value={hours(data.unitHours)} mono />
          <PfSpec
            label="Progress"
            value={p ? `${withUnit(p.from, p.unit)} → ${withUnit(p.to, p.unit)} (${withUnit(p.length, p.unit)})` : '—'}
            mono
          />
        </dl>
        {data.dailyLogId && (
          <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
            From the{' '}
            <Link className="font-bold text-fieldiq-text underline" to={`/pipeline-facility/daily-logs/${data.dailyLogId}`}>
              daily log
            </Link>{' '}
            for this day.
          </p>
        )}
      </PfPanel>
      <PfPanel eyebrow="Daily summary" title="Crew" testId="pf-ai-daily-crew">
        <AiTable
          rows={crew}
          testId="pf-ai-crew"
          rowKey={(c, i) => `${c.userId}:${i}`}
          empty="No crew hours were logged that day."
          cols={[
            { key: 'name', header: 'Name', render: (c) => <span className="font-semibold text-white">{text(c.name)}</span> },
            { key: 'role', header: 'Crew role', render: (c) => catalogLabel(crewRoles, c.role) },
            { key: 'hours', header: 'Hours', align: 'right', render: (c) => hours(c.hours) },
            { key: 'standby', header: 'Standby', align: 'right', render: (c) => hours(c.standbyHours) },
          ]}
        />
      </PfPanel>
      <PfPanel eyebrow="Daily summary" title="Owned units" testId="pf-ai-daily-units">
        <AiTable
          rows={units}
          testId="pf-ai-unit"
          rowKey={(u, i) => `${u.equipmentId}:${i}`}
          empty="No owned unit hours were logged that day."
          cols={[
            { key: 'unit', header: 'Unit', render: (u) => <span className="font-semibold text-white">{text(u.label)}</span> },
            { key: 'hours', header: 'Hours', align: 'right', render: (u) => hours(u.hours) },
            { key: 'standby', header: 'Standby', align: 'right', render: (u) => hours(u.standbyHours) },
          ]}
        />
      </PfPanel>
      <PfPanel eyebrow="Daily summary" title="Rent-ins on rent" testId="pf-ai-daily-rentins">
        <AiTable
          rows={rentIns}
          testId="pf-ai-rentin"
          rowKey={(r, i) => `${r.rentInId}:${i}`}
          empty="No rented-in unit was on rent that day."
          cols={[
            { key: 'unit', header: 'Unit', render: (r) => <span className="font-semibold text-white">{text(r.label)}</span> },
            { key: 'vendor', header: 'Vendor', render: (r) => text(r.vendor) },
            { key: 'billable', header: 'Billable', render: (r) => (typeof r.billable === 'boolean' ? (r.billable ? 'Yes' : 'No') : '—') },
            { key: 'hours', header: 'Hours', align: 'right', render: (r) => hours(r.hours) },
            { key: 'standby', header: 'Standby', align: 'right', render: (r) => hours(r.standbyHours) },
          ]}
        />
      </PfPanel>
      <PfPanel eyebrow="Daily summary" title="Digs" testId="pf-ai-daily-digs">
        <AiTable
          rows={digs}
          testId="pf-ai-dig"
          rowKey={(d, i) => `${d.digNumber}:${d.event}:${i}`}
          empty="No dig was opened or closed that day."
          minWidth="min-w-[360px]"
          cols={[
            { key: 'dig', header: 'Dig', render: (d) => <span className="font-mono">{text(d.digNumber)}</span> },
            { key: 'event', header: 'What happened', render: (d) => pfDigEventLabel(d.event) },
          ]}
        />
      </PfPanel>
      <PfPanel eyebrow="From the daily log" title="The crew's notes">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2">
          <div className="min-w-0">
            <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Work narrative</dt>
            <dd className="mt-1 whitespace-pre-wrap text-sm text-fieldiq-text">{text(data.narrative)}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Safety notes</dt>
            <dd className="mt-1 whitespace-pre-wrap text-sm text-fieldiq-text">{text(data.safetyNotes)}</dd>
          </div>
        </dl>
      </PfPanel>
    </div>
  );
}

// ─── Preflight ───────────────────────────────────────────────────────────────

const READY_SKIN = { label: 'Ready', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' };
const NOT_READY_SKIN = { label: 'Not ready', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' };

const gapCols = (first: string): Col<PfPreflightRateGap>[] => [
  { key: 'label', header: first, render: (g) => <span className="font-semibold text-white">{text(g.label)}</span> },
  { key: 'days', header: 'Days', render: (g) => days(g.days) },
  { key: 'why', header: 'Why', render: (g) => text(g.reason) },
];

export function PreflightFigures({ data }: { data: PfPreflightData }) {
  const unpriced = arr<PfPreflightData['unpricedLines'][number]>(data.unpricedLines);
  const logs = arr<PfPreflightData['logsWithoutTicket'][number]>(data.logsWithoutTicket);
  const crew = arr<PfPreflightRateGap>(data.crewWithoutRate);
  const units = arr<PfPreflightRateGap>(data.unitsWithoutRate);
  const rentIns = arr<PfPreflightRateGap>(data.rentInsWithoutRate);
  const title = data.ticket?.ticketNumber || (data.job?.jobNumber ? `${data.job.jobNumber} (whole job)` : 'Preflight');
  return (
    <div className="space-y-4" data-testid="pf-ai-preflight-figures">
      <PfPanel eyebrow="Preflight" title={title}>
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Ready to send</dt>
            <dd className="mt-1" data-testid="pf-ai-preflight-ready">
              {typeof data.ready === 'boolean' ? <PfBadge skin={data.ready ? READY_SKIN : NOT_READY_SKIN} value={String(data.ready)} /> : '—'}
            </dd>
          </div>
          <div className="min-w-0" data-testid="pf-ai-preflight-flags">
            <PfSpec label="Flags" value={displayValue(data.flagCount ?? undefined)} mono />
          </div>
          <PfSpec label="Tickets checked" value={displayValue(data.ticketsChecked ?? undefined)} mono />
          <PfSpec label="Currency" value={text(data.currency)} />
        </dl>
      </PfPanel>
      <PfPanel eyebrow="Preflight" title="Unpriced lines" testId="pf-ai-unpriced">
        <AiTable
          rows={unpriced}
          testId="pf-ai-unpriced-line"
          rowKey={(l) => l.lineId}
          rail={() => 'border-l-fi-amber'}
          empty="Every line has a price."
          cols={[
            { key: 'ticket', header: 'Ticket', render: (l) => (l.ticketId ? <Link className="font-mono underline" to={`/pipeline-facility/tickets/${l.ticketId}`}>{text(l.ticketNumber)}</Link> : '—') },
            { key: 'line', header: 'Line', render: (l) => <span className="font-semibold text-white">{text(l.description)}</span> },
            { key: 'why', header: 'Why', render: (l) => text(l.reason) },
          ]}
        />
      </PfPanel>
      <PfPanel eyebrow="Preflight" title="Daily logs on no ticket" testId="pf-ai-unticketed">
        <AiTable
          rows={logs}
          testId="pf-ai-unticketed-log"
          rowKey={(l) => l.dailyLogId}
          rail={() => 'border-l-fi-amber'}
          empty="Every daily log on this job is on a ticket."
          minWidth="min-w-[360px]"
          cols={[
            { key: 'day', header: 'Work date', render: (l) => <Link className="underline" to={`/pipeline-facility/daily-logs/${l.dailyLogId}`}>{pfDay(l.workDate)}</Link> },
          ]}
        />
        {data.scope === 'ticket' && logs.length > 0 && (
          <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">These are the job's other days; they do not stop this ticket being sent.</p>
        )}
      </PfPanel>
      <PfPanel eyebrow="Preflight" title="Crew with no rate" testId="pf-ai-crew-gaps">
        <AiTable rows={crew} testId="pf-ai-crew-gap" rowKey={(g) => g.id} rail={() => 'border-l-fi-amber'} empty="Every crew member has a rate on the card." cols={gapCols('Crew member')} />
      </PfPanel>
      <PfPanel eyebrow="Preflight" title="Units with no rate" testId="pf-ai-unit-gaps">
        <AiTable rows={units} testId="pf-ai-unit-gap" rowKey={(g) => g.id} rail={() => 'border-l-fi-amber'} empty="Every owned unit has a rate on the card." cols={gapCols('Unit')} />
      </PfPanel>
      <PfPanel eyebrow="Preflight" title="Rent-ins with no rate" testId="pf-ai-rentin-gaps">
        <AiTable rows={rentIns} testId="pf-ai-rentin-gap" rowKey={(g) => g.id} rail={() => 'border-l-fi-amber'} empty="Every billable rent-in has a billing rate." cols={gapCols('Rent-in')} />
      </PfPanel>
    </div>
  );
}

// ─── Compliance digest ───────────────────────────────────────────────────────

const stateCell = (s: string | null | undefined) => <PfBadge skin={pfAiStateSkin(s)} value={s ?? ''} />;
const jobsCell = (list: unknown) => {
  const j = arr<string>(list);
  return j.length ? j.join(', ') : '—';
};

export function ComplianceFigures({ data }: { data: PfComplianceDigestData }) {
  const oneCall = arr<PfComplianceDigestData['oneCall'][number]>(data.oneCall);
  const crew = arr<PfComplianceDigestData['crewCerts'][number]>(data.crewCerts);
  const equipment = arr<PfComplianceDigestData['equipment'][number]>(data.equipment);
  const skipped = arr<PfComplianceDigestData['skipped'][number]>(data.skipped);
  return (
    <div className="space-y-4" data-testid="pf-ai-compliance-figures">
      <PfPanel eyebrow="Compliance digest" title="Open pipeline work">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-4">
          <PfSpec label="As of" value={pfDay(data.today)} />
          <PfSpec label="Items" value={displayValue(data.total ?? undefined)} mono />
          <PfSpec label="One-call tickets" value={displayValue(oneCall.length)} mono />
          <PfSpec label="Crew certs" value={displayValue(crew.length)} mono />
        </dl>
      </PfPanel>
      <PfPanel eyebrow="Compliance" title="One-call / locate tickets" testId="pf-ai-onecall">
        <AiTable
          rows={oneCall}
          testId="pf-ai-onecall-item"
          rowKey={(x, i) => `${x.digNumber}:${i}`}
          rail={(x) => pfAiStateSkin(x.state).rail}
          empty="No one-call ticket on an open dig is expired, due or missing."
          cols={[
            { key: 'dig', header: 'Dig', render: (x) => <span className="font-mono">{text(x.digNumber)}</span> },
            { key: 'job', header: 'Job', render: (x) => <span className="font-mono">{text(x.jobNumber)}</span> },
            { key: 'ticket', header: 'Locate ticket', render: (x) => text(x.ticket) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Valid to', render: (x) => pfDay(x.dueOn) },
          ]}
        />
      </PfPanel>
      <PfPanel eyebrow="Compliance" title="Crew certificates" testId="pf-ai-certs">
        <AiTable
          rows={crew}
          testId="pf-ai-cert-item"
          rowKey={(x, i) => `${x.userId}:${x.cert}:${i}`}
          rail={(x) => pfAiStateSkin(x.state).rail}
          empty="Everyone crewed on open work holds current ground-disturbance and H2S certs."
          cols={[
            { key: 'name', header: 'Crew member', render: (x) => <span className="font-semibold text-white">{text(x.name)}</span> },
            { key: 'cert', header: 'Certificate', render: (x) => text(x.cert) },
            { key: 'jobs', header: 'Jobs', render: (x) => jobsCell(x.jobNumbers) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Expires', render: (x) => pfDay(x.dueOn) },
          ]}
        />
      </PfPanel>
      <PfPanel eyebrow="Compliance" title="Equipment on open jobs" testId="pf-ai-equipment">
        <AiTable
          rows={equipment}
          testId="pf-ai-equipment-item"
          rowKey={(x, i) => `${x.equipmentId}:${i}`}
          rail={(x) => pfAiStateSkin(x.state).rail}
          empty="No unit on an open job is due an inspection."
          cols={[
            { key: 'unit', header: 'Unit', render: (x) => <span className="font-semibold text-white">{text(x.label)}</span> },
            { key: 'jobs', header: 'Jobs', render: (x) => jobsCell(x.jobNumbers) },
            { key: 'state', header: 'State', render: (x) => stateCell(x.state) },
            { key: 'due', header: 'Cert expires', render: (x) => pfDay(x.dueOn) },
            {
              key: 'days',
              header: 'Working days',
              align: 'right',
              render: (x) => (x.daysUsed === null && x.dayLimit === null ? '—' : `${displayValue(x.daysUsed ?? undefined)} of ${displayValue(x.dayLimit ?? undefined)}`),
            },
          ]}
        />
      </PfPanel>
      {skipped.length > 0 && (
        <ul className="space-y-1 text-xs text-fieldiq-muted" data-testid="pf-ai-skipped">
          {skipped.map((s) => (
            <li key={s.check}>Not checked: {text(s.reason)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The figures for any run, by agent. Unknown agent or missing data says so rather than guessing. */
export function RunFigures({ agent, data }: { agent: string; data: unknown }) {
  if (!data || typeof data !== 'object') {
    return <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 text-sm text-fieldiq-text">The figures for this run were not stored.</p>;
  }
  if (agent === 'pf_daily_summary') return <DailySummaryFigures data={data as PfDailySummaryData} />;
  if (agent === 'pf_ticket_preflight') return <PreflightFigures data={data as PfPreflightData} />;
  if (agent === 'pf_compliance_digest') return <ComplianceFigures data={data as PfComplianceDigestData} />;
  return <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 text-sm text-fieldiq-text">This kind of draft is not shown here.</p>;
}

// ─── The AI draft and its review ─────────────────────────────────────────────

/**
 * The heading says where the draft stands. A PENDING draft is waiting on a
 * person and is not published; nothing on this screen reads it as anything
 * else.
 */
export const PF_DRAFT_HEADING: Record<string, string> = {
  pending: 'Draft — awaiting your approval',
  actioned: 'Approved',
  dismissed: 'Draft — dismissed',
  superseded: 'Draft — replaced by a newer run',
  no_action: 'Draft — no action',
};

function DraftText({ draft, testId }: { draft: PfAiDraft; testId: string }) {
  const obs = arr<string>(draft.observations);
  return (
    <div className="space-y-3" data-testid={testId}>
      <p className="whitespace-pre-wrap" data-testid={`${testId}-narrative`}>{text(draft.narrative)}</p>
      {obs.length > 0 && (
        <ul className="list-disc space-y-1 pl-5" data-testid={`${testId}-observations`}>
          {obs.map((o, i) => (
            <li key={i}>{o}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The AI's text. Approve / Edit / Dismiss only on a PENDING run and only for a
 * role that may review (the API refuses anyone else, and refuses a decided run
 * with 409 not_pending). Editing happens on the page: the narrative, and one observation
 * per line. A null draft is said plainly with the API's reason, and a person
 * may still write the narrative and approve that.
 */
export function PfAiDraftPanel({
  runId,
  outcome,
  draft,
  approved,
  approvedEdited,
  aiError,
  canReview,
}: {
  runId: string;
  outcome: string | null | undefined;
  draft: PfAiDraft | null | undefined;
  approved?: PfAiDraft | null;
  approvedEdited?: boolean | null;
  aiError: string | null | undefined;
  canReview: boolean;
}) {
  const review = useReviewPfAi();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [narrative, setNarrative] = useState('');
  const [observations, setObservations] = useState('');
  const pending = outcome === 'pending';
  const heading = (outcome && PF_DRAFT_HEADING[outcome]) || 'Draft';
  const busy = review.isPending;

  const startEdit = () => {
    setError(null);
    setNarrative(draft?.narrative ?? '');
    setObservations(arr<string>(draft?.observations).join('\n'));
    setEditing(true);
  };

  const approve = (edit?: { narrative: string; observations: string[] }) => {
    setError(null);
    review.mutate(
      { id: runId, action: 'approve', ...(edit ? { edit } : {}) },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Draft approved');
        },
        onError: (err) => setError(pfAiRefusal(err, 'The draft was not approved.')),
      },
    );
  };

  const dismiss = () => {
    setError(null);
    review.mutate(
      { id: runId, action: 'dismiss' },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Draft dismissed');
        },
        onError: (err) => setError(pfAiRefusal(err, 'The draft was not dismissed.')),
      },
    );
  };

  const submitEdit = () =>
    approve({
      narrative,
      observations: observations.split('\n').map((o) => o.trim()).filter(Boolean),
    });

  return (
    <section data-testid="pf-ai-draft" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-blue bg-fieldiq-card">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">AI draft</p>
          <h2 className="mt-1 text-base font-extrabold tracking-tight text-white" data-testid="pf-ai-draft-heading">
            {heading}
          </h2>
        </div>
        {canReview && pending && !editing && (
          <div className="flex flex-wrap gap-2" data-testid="pf-ai-review-actions">
            <button type="button" className={PF_SECONDARY} onClick={dismiss} disabled={busy} data-testid="pf-ai-dismiss">
              <XCircle className="size-4" aria-hidden="true" />
              Dismiss
            </button>
            <button type="button" className={PF_SECONDARY} onClick={startEdit} disabled={busy} data-testid="pf-ai-edit">
              <Pencil className="size-4" aria-hidden="true" />
              {draft ? 'Edit, then approve' : 'Write and approve'}
            </button>
            {draft && (
              <button type="button" className={PF_PRIMARY} onClick={() => approve()} disabled={busy} data-testid="pf-ai-approve">
                {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
                Approve
              </button>
            )}
          </div>
        )}
      </header>
      {pending && (
        <p className="border-b border-fieldiq-border bg-fi-signal-amber/10 px-4 py-2 text-xs text-fieldiq-text" data-testid="pf-ai-not-published">
          Not approved yet. Nothing has been sent, filed or published — this draft waits for a person to approve or dismiss it.
        </p>
      )}
      {error && (
        <p role="alert" className="border-b border-fieldiq-border bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text" data-testid="pf-ai-review-error">
          {error}
        </p>
      )}
      <div className="space-y-3 px-4 py-4 text-sm text-fieldiq-text">
        {editing ? (
          <div className="space-y-3" data-testid="pf-ai-edit-form">
            <PfField label="Summary" hint={`Up to ${PF_AI_MAX_NARRATIVE} characters.`}>
              <textarea
                className={`${PF_INPUT} h-32 py-2`}
                aria-label="Summary"
                maxLength={PF_AI_MAX_NARRATIVE}
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                data-testid="pf-ai-edit-narrative"
              />
            </PfField>
            <PfField label="Observations" hint={`One per line; up to 8, each up to ${PF_AI_MAX_OBSERVATION} characters.`}>
              <textarea
                className={`${PF_INPUT} h-24 py-2`}
                aria-label="Observations"
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                data-testid="pf-ai-edit-observations"
              />
            </PfField>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={PF_SECONDARY} onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className={PF_PRIMARY} onClick={submitEdit} disabled={busy} data-testid="pf-ai-edit-approve">
                {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
                Approve this text
              </button>
            </div>
          </div>
        ) : outcome === 'actioned' && approved ? (
          <>
            <DraftText draft={approved} testId="pf-ai-approved" />
            <p className="text-xs text-fieldiq-muted">{approvedEdited ? 'Edited by the reviewer before approval.' : 'Approved as the AI drafted it.'}</p>
          </>
        ) : draft ? (
          <>
            <DraftText draft={draft} testId="pf-ai-text" />
            <p className="text-xs text-fieldiq-muted">Written by AI from the figures above, as an explanation only. It changes no figure.</p>
          </>
        ) : (
          <div className="space-y-1" data-testid="pf-ai-no-draft">
            <p className="font-bold">AI draft unavailable — the figures above are still correct.</p>
            {aiError && <p className="text-xs text-fieldiq-muted" data-testid="pf-ai-error">{aiError}</p>}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── A run just made, shown in place (ticket page, job page) ─────────────────

/**
 * A preflight just run. The POST answers without an outcome, so the stored run
 * is read for it (and re-read after a review); until that loads, a fresh run
 * is pending by definition.
 */
export function PfAiRunInPlace({ result, canReview }: { result: PfAiRunResult; canReview: boolean }) {
  const stored = usePfAiDigest(result.agentRunId);
  const s = stored.data;
  return (
    <div className="space-y-4" data-testid="pf-ai-in-place">
      <RunFigures agent={String(result.agent)} data={result.data} />
      <PfAiDraftPanel
        runId={result.agentRunId}
        outcome={s?.outcome ?? 'pending'}
        draft={s ? s.draft : result.draft}
        approved={s?.approved ?? null}
        approvedEdited={s?.approvedEdited ?? null}
        aiError={s ? s.aiError : result.aiError}
        canReview={canReview}
      />
      <p className="text-xs text-fieldiq-muted">
        Kept with the{' '}
        <Link to={`/pipeline-facility/ai/${result.agentRunId}`} className="font-bold text-fieldiq-text underline" data-testid="pf-ai-run-link">
          AI drafts
        </Link>
        .
      </p>
    </div>
  );
}

/** The ticket page's Preflight button (band). Offered on any ticket the customer has not approved. */
export const pfTicketPreflightable = (status: string | null | undefined) => !!status && status !== 'customer_approved';

export function PfPreflightButton({ busy, onClick, className, testId }: { busy: boolean; onClick: () => void; className: string; testId: string }) {
  return (
    <button type="button" className={className} onClick={onClick} disabled={busy} data-testid={testId}>
      {busy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <ListChecks className="size-4" aria-hidden="true" />}
      {busy ? 'Checking…' : 'Preflight'}
    </button>
  );
}

// ─── Job page launchers ──────────────────────────────────────────────────────

/**
 * Daily logs tab: pick a day that HAS a log, draft its summary, open the draft.
 * Only logged days are offered, so 409 no_daily_log can only come from a log
 * deleted meanwhile — and then it reads in the API's words.
 */
export function PfDailySummaryLauncher({ jobId }: { jobId: string }) {
  const navigate = useNavigate();
  const logs = usePfDailyLogs(jobId);
  const run = useRunPfAi();
  const [day, setDay] = useState('');
  const [error, setError] = useState<string | null>(null);
  const daysLogged = Array.from(new Set((logs.data?.data ?? []).map((l) => l.workDate).filter((d): d is string => typeof d === 'string' && !!d)))
    .sort()
    .reverse();
  const picked = day || daysLogged[0] || '';
  if (!logs.isLoading && daysLogged.length === 0) return null;

  const go = () => {
    if (!picked) return;
    setError(null);
    run.mutate(
      { agent: 'pf_daily_summary', jobId, workDate: picked },
      {
        onSuccess: (r) => {
          if (r?.agentRunId) navigate(`/pipeline-facility/ai/${r.agentRunId}`);
        },
        onError: (err) => setError(pfAiRefusal(err, 'The daily summary did not run.')),
      },
    );
  };

  return (
    <PfPanel eyebrow="AI draft" title="Daily summary" testId="pf-ai-daily-launcher">
      <div className="space-y-3 px-4 py-4">
        <PfFormError message={error} />
        <p className="text-xs text-fieldiq-muted">
          Totals the day's log exactly, then asks the AI to draft a summary. The draft waits for your approval; nothing is sent.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-64">
            <PfField label="Day">
              <select className={PF_INPUT} aria-label="Day to summarise" value={picked} onChange={(e) => setDay(e.target.value)} disabled={logs.isLoading || run.isPending}>
                {daysLogged.map((d) => (
                  <option key={d} value={d}>
                    {pfDay(d)}
                  </option>
                ))}
              </select>
            </PfField>
          </div>
          <button type="button" className={PF_PRIMARY} onClick={go} disabled={!picked || run.isPending} data-testid="pf-ai-run-daily">
            {run.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Sparkles className="size-4" aria-hidden="true" />}
            {run.isPending ? 'Drafting…' : 'Draft daily summary'}
          </button>
        </div>
      </div>
    </PfPanel>
  );
}

/** Tickets tab: preflight the whole job (its tickets not yet customer-approved, and its logs). */
export function PfJobPreflight({ jobId, canReview }: { jobId: string; canReview: boolean }) {
  const run = useRunPfAi();
  const [result, setResult] = useState<PfAiRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const go = () => {
    setError(null);
    run.mutate(
      { agent: 'pf_ticket_preflight', jobId },
      {
        onSuccess: (r) => setResult(r ?? null),
        onError: (err) => setError(pfAiRefusal(err, 'The preflight did not run.')),
      },
    );
  };
  return (
    <div className="space-y-4" data-testid="pf-ai-job-preflight">
      <PfPanel
        eyebrow="Before sending"
        title="Job preflight"
        actions={<PfPreflightButton busy={run.isPending} onClick={go} className={PF_SECONDARY} testId="pf-ai-run-job-preflight" />}
      >
        <div className="space-y-2 px-4 py-4">
          <PfFormError message={error} />
          <p className="text-xs text-fieldiq-muted">
            Checks this job's open tickets and daily logs for unpriced lines, days on no ticket, and crew, units or rent-ins with no rate. The AI's text explains the flags; it changes nothing.
          </p>
        </div>
      </PfPanel>
      {result && <PfAiRunInPlace result={result} canReview={canReview} />}
    </div>
  );
}

